import { useDispatch, useSelector } from "react-redux"
import { openAutoModeEval } from "@data/redux/actions/liveMode"

// Auto-mode targets every *group* session — anything that is not a 1-on-1
// ("individual") nor a prospect demo ("trial"). We use a denylist rather than
// an allowlist of group types (biweekly, study_group, holiday_workbook,
// custom, support…) so any current or future group format is covered without a
// code change. A missing/unknown type never triggers (defensive).
const EXCLUDED_SESSION_TYPES = ["individual", "trial"]

const isAutoModeEligibleSession = (liveSession) => {
    const type = liveSession?.live_room?.type
    return !!type && !EXCLUDED_SESSION_TYPES.includes(type)
}

const hasEmptyTodoQueue = (currentUser) => {
    const todos = currentUser?.todo_activities ?? []
    return todos.filter((t) => !t?.isDeleted).length === 0
}

// Returns a function `(finalizedActivity) => boolean` that opens the auto-eval
// overlay if the student is in a live group session, the just-finished activity
// belongs to them, and their todo queue is empty.
//
// Called from CongratsScreen.proceedWithNavigation (when the student taps
// "ACTIVITÉ SUIVANTE" or the 12s timer expires) so the congrats screen plays
// before the overlay takes over. Returns `true` when auto-mode took over so
// the caller can short-circuit its own navigation.
const useAutoModeMaybeOpen = () => {
    const dispatch = useDispatch()
    const isLive = useSelector((s) => s.liveMode?.isLiveModeEnabled)
    const liveSessionId = useSelector((s) => s.liveMode?.liveSessionId)
    const currentUser = useSelector((s) => s.data?.currentUser?.object)
    const soundModeIds = useSelector((s) => s.liveMode?.soundModeIds)
    const currentLiveSession = currentUser?.live_sessions?.find(
        (x) => x?.id === liveSessionId
    )

    return (finalizedActivity) => {
        if (!finalizedActivity?.id) return false
        if (!isLive) return false
        if (!isAutoModeEligibleSession(currentLiveSession)) return false
        if (finalizedActivity.student_id !== currentUser?.id) return false
        if (!hasEmptyTodoQueue(currentUser)) return false
        // soundModeIds is the list of student IDs in dialogue mode. When the
        // student is in dialogue with the tutor, auto-mode must not steal
        // their attention by suggesting a next activity in parallel.
        if (soundModeIds?.includes(currentUser?.id)) return false
        dispatch(openAutoModeEval(finalizedActivity))
        return true
    }
}

export default useAutoModeMaybeOpen
